import React from 'react';
import { Wind, Radio } from 'lucide-react';

const COLOR_MAP = {
  green:  { fg: '#168162', bg: '#D2F4E5', accent: '#3FC79A' },
  yellow: { fg: '#8D6912', bg: '#FFF1C2', accent: '#F6C84F' },
  orange: { fg: '#A52828', bg: '#FFDADA', accent: '#FF8C8C' },
  red:    { fg: '#A52828', bg: '#FFDADA', accent: '#F25555' },
  purple: { fg: '#5722B5', bg: '#EEEAFF', accent: '#8E24AA' },
  maroon: { fg: '#5C1717', bg: '#FFDADA', accent: '#7E1F1F' },
  gray:   { fg: '#334155', bg: '#F1F5F9', accent: '#9AAFD7' },
};

function distLabel(m) {
  if (m == null) return '—';
  if (m < 1000) return `${m} m away`;
  return `${(m / 1000).toFixed(1)} km away`;
}

export default function AQICard({ data, loading }) {
  if (loading) {
    return (
      <div className="puvio-card p-5" data-testid="aqi-card-skeleton">
        <div className="puvio-skeleton h-6 w-28 mb-3" />
        <div className="puvio-skeleton h-10 w-24" />
        <div className="puvio-skeleton h-2 w-full mt-4" />
      </div>
    );
  }
  if (!data) return null;
  const local = data.local_sensor;
  const primaryAqi = local?.us_aqi ?? data.us_aqi ?? data.european_aqi;
  const primaryBucket = local?.bucket || data.bucket || { color: 'gray', label: 'Unknown' };
  const C = COLOR_MAP[primaryBucket.color] || COLOR_MAP.gray;
  const pct = primaryAqi != null ? Math.min(100, Math.round((primaryAqi / 300) * 100)) : 0;
  const isHyper = !!local;

  return (
    <div className="puvio-card p-5 puvio-card-interactive" data-testid="aqi-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Air Quality</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">{primaryBucket.label}</p>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: C.bg }}>
          <Wind className="w-6 h-6" style={{ color: C.fg }} />
        </div>
      </div>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-display text-5xl font-extrabold tracking-tight text-[#0F1A13]" data-testid="aqi-value">{primaryAqi != null ? Math.round(primaryAqi) : '—'}</span>
        <span className="text-[11px] text-[var(--puvio-text-2)] font-bold mb-2">US AQI</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[var(--puvio-bg-soft)] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: C.accent }} />
      </div>
      {isHyper ? (
        <div className="mt-3 p-2.5 rounded-2xl bg-[var(--puvio-success-bg)] border border-mint-200 flex items-start gap-2" data-testid="aqi-hyperlocal">
          <span className="w-6 h-6 rounded-full bg-[var(--puvio-mint-deep)] text-[#0F1A13] flex items-center justify-center shrink-0 mt-0.5">
            <Radio className="w-3 h-3" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--puvio-success-fg)]">Hyperlocal</p>
            <p className="text-[11px] text-[var(--puvio-text-2)] truncate font-semibold">{distLabel(local.distance_m)}</p>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-[11px] text-[var(--puvio-text-2)] font-semibold">PM2.5 {data.pm2_5 != null ? data.pm2_5.toFixed(1) : '—'} µg/m³</p>
      )}
    </div>
  );
}
